import { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, HardHat } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Field, Input, Select, Textarea } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { PhotoCaptureField } from "@/features/assets/PhotoCaptureField";
import { uploadAssetPhoto } from "@/features/assets/assetPhotoUpload";

// =============================================================================
// RiggingNewScreen — /assets/rigging/new (HR hub, manager+) and
//                    /inspections/add/rigging (HSE hub, every employee)
// Create a rigging item. Uses the class + type lookup tables from migration
// 031 so every new entry picks from the canonical taxonomy. Open to any
// authenticated user so field crew on the road can self-serve (T-051 Part B).
//
// 12 Oct 2026 — the same screen is mounted under /inspections so crews can
// actually reach it: /assets resolves to the HR hub, which field and
// supervisor logins don't hold, so the "open to field crew" promise above was
// never true from a phone. When opened from /inspections the back and done
// links stay inside the register. Kit is a picker over the kits that already
// exist (free text is how "Rigging Kit 4" and "Rigging kit 4" became two
// kits). Who holds it is set by a manager or supervisor only — a crew member's
// new item lands in the Pool, unassigned, for a manager to allocate (James:
// "employees do not allocate any equipment, only managers"). The reference
// photo is manager+ only too: asset_photos is manager-write under RLS, so a
// crew upload would silently fail — a manager adds it from the item page.
// =============================================================================

const STATUSES = ["in_service", "quarantined", "destroyed", "retired"] as const;

interface LookupRow { code: string; display_name: string; }

export default function RiggingNewScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  // Opened from the HSE register (crew path) or from HR → Assets (manager path).
  const fromRegister = location.pathname.startsWith("/inspections");
  const backHref     = fromRegister ? "/inspections?kind=rigging" : "/assets";
  const canAllocate  = hasRole(user, "supervisor");
  const canPhoto     = hasRole(user, "manager");

  // T-253 — rigging_code is system-generated by the DB default (mig 145).
  const [display_name, setDisplayName] = useState("");
  const [equipment_class, setEquipClass] = useState("");
  const [equipment_type, setEquipType]   = useState("");
  const [make, setMake]        = useState("");
  const [model, setModel]      = useState("");
  const [serial_number, setSerial] = useState("");
  const [batch_lot_number, setBatchLot] = useState("");
  const [swl_kg, setSwl]          = useState("");
  const [rated_load_kn, setRatedLoad] = useState("");
  const [purchase_date, setPurchaseDate] = useState("");
  const [purchase_supplier, setSupplier] = useState("Height Dynamics");
  const [date_of_manufacture, setManDate] = useState("");
  const [in_service_date, setInServiceDate] = useState("");
  const [service_life_years, setServiceLife] = useState("10");
  const [destroy_by_date, setDestroyBy] = useState("");
  const [inspection_interval_months, setInspInterval] = useState("3");
  const [kit_name, setKitName] = useState("");
  const [assigned_to_employee_id, setAssignedTo] = useState("");
  const [status, setStatus] = useState<string>("in_service");
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  // ---- Lookups ------------------------------------------------------------
  const { data: classes = [] } = useQuery({
    queryKey: ["rigging_classes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rigging_equipment_classes")
        .select("class_code, display_name, sort_order")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []).map(r => ({ code: r.class_code, display_name: r.display_name })) as LookupRow[];
    },
  });

  const { data: types = [] } = useQuery({
    queryKey: ["rigging_types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rigging_equipment_types")
        .select("type_code, display_name, sort_order")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []).map(r => ({ code: r.type_code, display_name: r.display_name })) as LookupRow[];
    },
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees_active"],
    enabled: canAllocate,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees").select("employee_id, first_name, last_name")
        .eq("is_active", true).order("first_name");
      if (error) throw error;
      return data ?? [];
    },
  });

  // Kits that already exist — pick, never retype (brain: "Equipment register —
  // how Ops tracks and inspects gear", the Josh Thompson double-kit trap).
  const { data: kitRows = [] } = useQuery({
    queryKey: ["rigging_kits"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rigging_equipment").select("kit_name").not("kit_name", "is", null);
      if (error) throw error;
      return (data ?? []) as { kit_name: string | null }[];
    },
  });
  const kits = useMemo(() => {
    const set = new Set<string>();
    for (const r of kitRows) if (r.kit_name) set.add(r.kit_name);
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [kitRows]);

  const createMut = useMutation({
    mutationFn: async () => {
      // rigging_code intentionally omitted — DB default mints it (T-253, mig 145)
      if (!display_name.trim()) throw new Error("Display name is required.");
      const { data, error } = await supabase
        .from("rigging_equipment")
        .insert({
          // rigging_code: omitted — DB default mints it (mig 145)
          display_name: display_name.trim(),
          equipment_class: equipment_class || null,
          equipment_type: equipment_type || null,
          make: make.trim() || null,
          model: model.trim() || null,
          serial_number: serial_number.trim() || null,
          batch_lot_number: batch_lot_number.trim() || null,
          swl_kg: swl_kg ? parseInt(swl_kg, 10) : null,
          rated_load_kn: rated_load_kn ? parseFloat(rated_load_kn) : null,
          purchase_date: purchase_date || null,
          purchase_supplier: purchase_supplier.trim() || null,
          date_of_manufacture: date_of_manufacture || null,
          in_service_date: in_service_date || null,
          service_life_years: service_life_years ? parseInt(service_life_years, 10) : null,
          destroy_by_date: destroy_by_date || null,
          inspection_required: true,
          inspection_interval_months: inspection_interval_months ? parseInt(inspection_interval_months, 10) : 3,
          kit_name: canAllocate ? (kit_name.trim() || null) : null,
          assigned_to_employee_id: canAllocate ? (assigned_to_employee_id || null) : null,
          status,
          notes: notes.trim() || null,
        })
        .select("rigging_id")
        .single();
      if (error) throw error;
      const newId = data.rigging_id as string;
      // Upload the optional photo now that we have an ID. Swallow photo
      // errors — the item is already created; user can add photos from the
      // detail page if this failed (network hiccup, wrong format, etc).
      if (photo && canPhoto) {
        try { await uploadAssetPhoto("rigging", newId, photo); }
        catch (e) { console.warn("Rigging photo upload failed:", e); }
      }
      return newId;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["inspection_register"] });
      qc.invalidateQueries({ queryKey: ["rigging_kits"] });
      navigate(fromRegister ? `/inspections/equipment/rigging/${id}` : `/assets/rigging/${id}`);
    },
    onError: (e: any) => {
      const msg = e?.message
        ?? e?.error?.message
        ?? e?.details
        ?? (typeof e === "string" ? e : JSON.stringify(e));
      setErrMsg(`Create failed — ${msg}`);
      console.error("[RiggingNewScreen] create error", e);
    },
  });

  const canSubmit = !!display_name.trim() && !createMut.isPending;

  return (
    <div className="p-6 max-w-3xl mx-auto pb-28">
      <button onClick={() => navigate(backHref)} className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3" type="button">
        <ArrowLeft size={14} /> {fromRegister ? "Back to the register" : "Back to assets"}
      </button>
      <h1 className="text-[20px] font-semibold text-text flex items-center gap-2 mb-5">
        <HardHat size={20} className="text-accent" /> New rigging equipment
      </h1>

      <Card>
        {canPhoto && (
          <div className="p-5 border-b border-line">
            <PhotoCaptureField
              value={photo}
              onChange={setPhoto}
              label="Reference photo"
              hint="Snap it now so it's easy to identify in the register. You can add more photos later from the detail page."
            />
          </div>
        )}
        <div className="p-5 border-b border-line">
          <div className="text-[13px] font-medium text-text mb-3">Identity</div>
          {/* T-253 — code auto-generated by the DB on save (RG-NNNN). */}
          <div className="mb-3 px-3 py-2 rounded bg-accent/5 border border-accent/20 text-[12px] text-text-muted">
            <span className="font-mono text-accent">RG-NNNN</span> code generated automatically on save. No need to type one.
          </div>
          <Field label="Display name *">
            <Input value={display_name} onChange={e => setDisplayName(e.target.value)} placeholder="e.g. EXOFIT NEX TOWER WORKER" autoFocus />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Class" hint="Pick from the curated list; 'Other' if it doesn't fit.">
              <Select value={equipment_class} onChange={e => setEquipClass(e.target.value)}>
                <option value="">— select —</option>
                {classes.map(c => <option key={c.code} value={c.display_name}>{c.display_name}</option>)}
              </Select>
            </Field>
            <Field label="Type">
              <Select value={equipment_type} onChange={e => setEquipType(e.target.value)}>
                <option value="">— select —</option>
                {types.map(t => <option key={t.code} value={t.display_name}>{t.display_name}</option>)}
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Make">
              <Input value={make} onChange={e => setMake(e.target.value)} placeholder="3M SALA" />
            </Field>
            <Field label="Model">
              <Input value={model} onChange={e => setModel(e.target.value)} />
            </Field>
            <Field label="Serial number">
              <Input mono value={serial_number} onChange={e => setSerial(e.target.value)} />
            </Field>
          </div>
          <Field label="Batch / lot">
            <Input mono value={batch_lot_number} onChange={e => setBatchLot(e.target.value)} />
          </Field>
        </div>

        <div className="p-5 border-b border-line">
          <div className="text-[13px] font-medium text-text mb-3">Rating &amp; lifecycle</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SWL (kg)">
              <Input type="number" mono value={swl_kg} onChange={e => setSwl(e.target.value)} />
            </Field>
            <Field label="Rated load (kN)">
              <Input type="number" mono value={rated_load_kn} onChange={e => setRatedLoad(e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Purchase date">
              <Input type="date" mono value={purchase_date} onChange={e => setPurchaseDate(e.target.value)} />
            </Field>
            <Field label="Supplier">
              <Input value={purchase_supplier} onChange={e => setSupplier(e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date of manufacture">
              <Input type="date" mono value={date_of_manufacture} onChange={e => setManDate(e.target.value)} />
            </Field>
            <Field label="In-service date">
              <Input type="date" mono value={in_service_date} onChange={e => setInServiceDate(e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Service life (years)">
              <Input type="number" mono value={service_life_years} onChange={e => setServiceLife(e.target.value)} />
            </Field>
            <Field label="Destroy by">
              <Input type="date" mono value={destroy_by_date} onChange={e => setDestroyBy(e.target.value)} />
            </Field>
          </div>
        </div>

        <div className="p-5 border-b border-line">
          <div className="text-[13px] font-medium text-text mb-3">Compliance</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inspection interval (months)" hint="Industry standard 3 months for rigging.">
              <Input type="number" mono value={inspection_interval_months} onChange={e => setInspInterval(e.target.value)} />
            </Field>
            <Field label="Initial status">
              <Select value={status} onChange={e => setStatus(e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</option>)}
              </Select>
            </Field>
          </div>
        </div>

        <div className="p-5">
          <div className="text-[13px] font-medium text-text mb-3">Assignment &amp; notes</div>
          {canAllocate ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kit" hint="Pick an existing kit. Only use New kit for a genuinely new one — match the spelling of the others.">
                <Select
                  value={kit_name}
                  onChange={e => {
                    if (e.target.value === "__new__") {
                      const name = window.prompt("New kit name (match the spelling of the others exactly):")?.trim();
                      setKitName(name ?? "");
                    } else {
                      setKitName(e.target.value);
                    }
                  }}
                >
                  <option value="">— Pool (spare) —</option>
                  {(kits.includes(kit_name) || !kit_name ? kits : [...kits, kit_name].sort()).map(k => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                  <option value="__new__">New kit…</option>
                </Select>
              </Field>
              <Field label="Assigned to">
                <Select value={assigned_to_employee_id} onChange={e => setAssignedTo(e.target.value)}>
                  <option value="">— Pool —</option>
                  {employees.map(e => {
                    const name = `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim();
                    return <option key={e.employee_id} value={e.employee_id}>{name}</option>;
                  })}
                </Select>
              </Field>
            </div>
          ) : (
            <div className="mb-3 px-3 py-2 rounded bg-ink-raised border border-line text-[12px] text-text-muted">
              This item goes into the <span className="text-text">Pool</span>. A manager or supervisor allocates it to a kit and a person from HR → Assets.
            </div>
          )}
          <Field label="Notes" hint="Free text. Load rating specifics, supplier quirks, etc.">
            <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </Field>
        </div>
      </Card>

      <StickyBottomBar>
        {errMsg && (
          <div className="px-3 py-2 bg-red-900/30 border-b border-red-900/50 text-red-200 text-[12px] text-center">{errMsg}</div>
        )}
        <div className="p-3 flex justify-center">
          <div className="flex gap-2 max-w-2xl w-full">
            <Button variant="secondary" size="lg" onClick={() => navigate(backHref)} type="button">Cancel</Button>
            <Button size="lg" icon={CheckCircle2} onClick={() => createMut.mutate()} disabled={!canSubmit} className="flex-1 justify-center" type="button">
              {createMut.isPending ? "Creating…" : "Create rigging item"}
            </Button>
          </div>
        </div>
      </StickyBottomBar>
    </div>
  );
}
